import React, { createContext, useMemo, useReducer } from "react";
import IChildren from "../interfaces/IChildren";
import { clearSelectionsAction, fleetManagerContextTypes, SelectedDelegeeRowKey, setSelectedRowKeyAction } from "./store/actions/fleetManagerContext.types";
import fleetManagerContextReducer, {
  AssignmentType,
  DelegationType,
  FleetManagerContextState,
  initialState,
} from "./store/reducers/fleetManagerContext.reducer";
import { AssignedVehicleRow } from "../containers/FleetManager/MyProducts/ParkingRightsDataTable/VehiclesTable";
import { togglePedestrianDelegeeEditFormAction, toggleShowEntryRightInvitationFormAction } from "../store/actions/fleetManagerContext.actions";


export type FleetManagerContextType = {
  fleetManagerState: FleetManagerContextState;
  setSelectedDelegeeId: (registrationId: string) => void;
  setSelectedDelegeeRowKey: (rowKey: SelectedDelegeeRowKey) => void;
  clearSelections: () => void;
  setSelectedParkingRightIds: (parkingRightIds: any[]) => void;
  setDelegationType: (delegationType: DelegationType) => void;
  setAssignmentType: (assignmentType: AssignmentType) => void;
  setDragAndDropView: (show: boolean) => void;
  setVehicleDragAndDropView: (show: boolean) => void;
  setNumberPlateWarning: (warning: string | undefined) => void;
  setNumberPlateError: (error: string | undefined) => void;
  toggleAddDelegee: () => void;
  toggleAssignVehicle: () => void;
  toggleMultipleVehicles: () => void;
  closeMultipleVehicles: () => void;
  toggleEditVehicleAssignment: (vehicleAssignment?: AssignedVehicleRow) => void;
  toggleEditDelegee: (registrationId: string) => void;
  toggleDeleteDelegeeWarning: () => void;
  toggleBatchRevokeParkingRightsWarning: () => void;
  toggleBatchRevokeVehicleAssignmentsWarning: () => void;
  toggleRevokeParkingRightsWarning: () => void;
  toggleRevokeVehicleAssignmentsWarning: (vehicleAssignment?: AssignedVehicleRow) => void;
  toggleDeleteVehicleAssignmentsWarning: (vehicleAssignment?: AssignedVehicleRow) => void;
  toggleBatchDeleteDelegationsWarning: () => void;
  toggleCloseImportWarning: () => void;
  resetContext: () => void;
  toggleEntryRightInvitationForm: () => void;
  togglePedestrianEditForm: (delegeeId: string) => void;
};

export const FleetManagerContext = createContext<FleetManagerContextType>(
  {} as FleetManagerContextType
);


const FleetManagerContextProvider = ({ children }: IChildren) => {
  const [fleetManagerState, dispatch] = useReducer(
    fleetManagerContextReducer,
    initialState,
  );

  const setSelectedRegistrationId = (registrationId: string) =>
    dispatch({
      type: fleetManagerContextTypes.SET_SELECTED_REGISTRATION_ID,
      payload: registrationId,
    });

  const setSelectedDelegeeRowKey = (rowKey: SelectedDelegeeRowKey) =>
    dispatch(setSelectedRowKeyAction(rowKey));

  const setSelectedParkingRightIds = (parkingRightIds: any[]) =>
    dispatch({
      type: fleetManagerContextTypes.SET_SELECTED_PARKINGRIGHT_IDS,
      payload: parkingRightIds,
    });

  const setDelegationType = (delegationType: DelegationType) =>
    dispatch({
      type: fleetManagerContextTypes.SET_DELEGATION_TYPE,
      payload: delegationType,
    });

  const setAssignmentType = (assignmentType: AssignmentType) =>
    dispatch({
      type: fleetManagerContextTypes.SET_ASSIGNMENT_TYPE,
      payload: assignmentType,
    });

  const setDragAndDropView = (show: boolean) =>
    dispatch({
      type: fleetManagerContextTypes.SET_DRAG_AND_DROP_VIEW,
      payload: show,
    });

  const setVehicleDragAndDropView = (show: boolean) =>
    dispatch({
      type: fleetManagerContextTypes.SET_VEHICLE_DRAG_AND_DROP_VIEW,
      payload: show,
    });

  const setNumberPlateWarning = (warning: string | undefined) =>
    dispatch({
      type: fleetManagerContextTypes.SET_NRPLATE_WARNING,
      payload: warning,
    });

  const setNumberPlateError = (error: string | undefined) =>
    dispatch({
      type: fleetManagerContextTypes.SET_NRPLATE_ERROR,
      payload: error,
    });

  const toggleAddDelegee = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_ADD_DELEGEE,
    });

  const toggleAssignVehicle = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_ASSIGN_VEHICLE,
    });

  const toggleMultipleVehicles = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_MULTIPLE_VEHICLES,
    });

  const toggleEntryRightInvitationForm = () => {
    dispatch(toggleShowEntryRightInvitationFormAction());
  }

  const togglePedestrianEditForm = (delegeeId: string) => {
    dispatch(togglePedestrianDelegeeEditFormAction({ delegeeId }));
  }

  const clearSelections = () => {
    dispatch(clearSelectionsAction());
  }

  const closeMultipleVehicles = () =>
    dispatch({
      type: fleetManagerContextTypes.CLOSE_MULTIPLE_VEHICLES,
    });

  const toggleEditVehicleAssignment = (vehicleAssignment?: AssignedVehicleRow) =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_EDIT_VEHICLE_ASSIGNMENT,
      payload: vehicleAssignment,
    });

  const toggleEditDelegee = (registrationId: string) =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_EDIT_DELEGEE,
      payload: registrationId,
    });

  const toggleDeleteDelegeeWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_DELETE_DELEGEE,
    });

  const toggleBatchRevokeParkingRightsWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_BATCH_REVOKE_PARKINGRIGHTS,
    });

  const toggleBatchRevokeVehicleAssignmentsWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_BATCH_REVOKE_VEHICLE_ASSIGNMENT,
    });

  const toggleRevokeParkingRightsWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_REVOKE_PARKINGRIGHTS,
    });

  const toggleRevokeVehicleAssignmentsWarning = (vehicleAssignment?: AssignedVehicleRow) =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_REVOKE_VEHICLE_ASSIGNMENT,
      payload: vehicleAssignment
    });

   const toggleDeleteVehicleAssignmentsWarning = (vehicleAssignment?: AssignedVehicleRow) =>
        dispatch({
            type: fleetManagerContextTypes.TOGGLE_SHOW_DELETE_VEHICLE_ASSIGNMENT,
            payload: vehicleAssignment
        });

  const toggleBatchDeleteDelegationsWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_BATCH_DELETE_DELEGATIONS,
    });

  const toggleCloseImportWarning = () =>
    dispatch({
      type: fleetManagerContextTypes.TOGGLE_SHOW_IMPORT_WARNING,
    });

  const resetContext = () =>
    dispatch({
      type: fleetManagerContextTypes.RESET_FLEET_MANAGER_CONTEXT,
    });

  const store: FleetManagerContextType = {
    fleetManagerState,
    setSelectedDelegeeId: setSelectedRegistrationId,
    setSelectedDelegeeRowKey,
    setSelectedParkingRightIds,
    setDelegationType,
    setAssignmentType,
    setNumberPlateWarning,
    setNumberPlateError,
    setDragAndDropView,
    setVehicleDragAndDropView,
    toggleAddDelegee,
    toggleAssignVehicle,
    toggleMultipleVehicles,
    closeMultipleVehicles,
    toggleEditVehicleAssignment,
    toggleEditDelegee,
    resetContext,
    toggleDeleteDelegeeWarning,
    toggleBatchRevokeParkingRightsWarning,
    toggleBatchRevokeVehicleAssignmentsWarning,
    toggleRevokeParkingRightsWarning,
    toggleRevokeVehicleAssignmentsWarning,
    toggleDeleteVehicleAssignmentsWarning,
    toggleBatchDeleteDelegationsWarning,
    toggleCloseImportWarning,
    toggleEntryRightInvitationForm,
    togglePedestrianEditForm,
    clearSelections
  };

  const memoStore = useMemo(
    () => ({
      store,
    }),
    [store]
  );

  return (
    <FleetManagerContext.Provider value={memoStore.store}>
      {children}
    </FleetManagerContext.Provider>
  );
};

export default FleetManagerContextProvider;
